import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Radar, Loader2, SlidersHorizontal, Plus } from 'lucide-react';
import { EditActionBar } from '../shared/EditActionBar';
import { ExportXlsxButton } from '../shared/ExportXlsxButton';
import type { Driver12Data, ClusteringOutlierRow } from '../../../api/apiTypesConductRisk';
import { useConductRiskDriver } from '../../../api/hooks/useConductRiskDriver';
import { fetchExportConductRiskDriverXlsx } from '../../../api/functionApi';

const numCls = 'w-24 text-right bg-white text-[#1B365D] border border-amber-300 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:border-amber-500 tabular-nums';
const txtCls = 'w-full min-w-[7rem] bg-white border border-amber-300 rounded px-1.5 py-0.5 text-xs text-[#1B365D] focus:outline-none focus:border-amber-500';

/** Soglia Modified Z-score sopra la quale un record e' outlier (spec PDF: 3.5). */
const Z_THRESHOLD = 3.5;

const fmtN2 = (n: number) => n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct = (n: number) => `${fmtN2(n)}%`;

type TabKey = 'top' | 'all';

const emptyClusteringOutlierRow = (): ClusteringOutlierRow => ({
  clone_uo:           '',
  branch_name:        '',
  regional_division:  '',
  area:               '',
  pct_ragg_annuo:     0,
  cluster_median_pct: 0,
  cluster_mad:        0,
  modified_z_score:   0,
});

const OutlierClusteringFilialiContainer = ({ reportId }: { reportId: string }) => {
  const { data: remoteData, loading, error, save, isReadOnly } = useConductRiskDriver<Driver12Data>(reportId, 12);

  const [data,       setData]       = useState<Driver12Data | null>(null);
  const [savedData,  setSavedData]  = useState<Driver12Data | null>(null);
  const [topN,       setTopN]       = useState<number>(10);
  const [autoSaving, setAutoSaving] = useState(false);
  const [isEditing,  setIsEditing]  = useState(false);
  const [tab,        setTab]        = useState<TabKey>('top');
  const debounceRef    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initializedRef = useRef(false);

  const handleAnnulla = () => { if (savedData) setData(savedData); setIsEditing(false); };
  const handleSalva   = async () => {
    if (!data) return;
    await save(data);
    setSavedData(data);
    setIsEditing(false);
  };

  /** Aggiorna un campo NUMERICO della riga (match per riferimento) sia in
   *  outliers che in rows. Editing libero, nessun ricalcolo lato FE. */
  const updateRowNum = (row: ClusteringOutlierRow, field: keyof ClusteringOutlierRow, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => {
      if (!prev) return prev;
      const patch = (rs: ClusteringOutlierRow[]) => rs.map(r => r === row ? { ...r, [field]: val } : r);
      return { ...prev, outliers: patch(prev.outliers), rows: patch(prev.rows) };
    });
  };
  /** Aggiorna un campo TESTUALE (cloneUo/filiale/direzione/area) della riga. */
  const updateRowText = (row: ClusteringOutlierRow, field: 'clone_uo' | 'branch_name' | 'regional_division' | 'area', value: string) => {
    setData(prev => {
      if (!prev) return prev;
      const patch = (rs: ClusteringOutlierRow[]) => rs.map(r => r === row ? { ...r, [field]: value } : r);
      return { ...prev, outliers: patch(prev.outliers), rows: patch(prev.rows) };
    });
  };

  /** Appende una riga vuota all'array sottostante del tab corrente. */
  const addRow = () => {
    setData(prev => {
      if (!prev) return prev;
      return tab === 'top'
        ? { ...prev, rows: [...prev.rows, emptyClusteringOutlierRow()] }
        : { ...prev, outliers: [...prev.outliers, emptyClusteringOutlierRow()] };
    });
  };

  useEffect(() => {
    if (!remoteData) return;
    // Difensivo: se il BE restituisce array null, normalizzo a [].
    const normalized: Driver12Data = {
      ...remoteData,
      outliers: remoteData.outliers ?? [],
      rows:     remoteData.rows     ?? [],
    };
    setData(normalized);
    setSavedData(normalized);
    if (!initializedRef.current) {
      setTopN(remoteData.top_n);
      initializedRef.current = true;
    }
  }, [remoteData]);

  // Autosave top_n (~600ms debounced). Attivo solo fuori editing.
  useEffect(() => {
    if (!data || !savedData || isReadOnly || isEditing) return;
    if (topN === savedData.top_n) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const payload: Driver12Data = { ...data, top_n: topN };
      setAutoSaving(true);
      try {
        await save(payload);
        setSavedData(payload);
      } catch (err) {
        console.error('Autosave top_n Driver 12 fallito:', err);
        setTopN(savedData.top_n);
      } finally {
        setAutoSaving(false);
      }
    }, 600);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [topN, savedData, isReadOnly, isEditing, data, save]);

  const rows: ClusteringOutlierRow[] = useMemo(() => {
    if (!data) return [];
    const src = tab === 'top' ? data.rows : data.outliers;
    return [...src].sort((a, b) => b.modified_z_score - a.modified_z_score);
  }, [data, tab]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-12 h-12 border-[5px] border-gray-200 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      </div>
    );
  }
  if (!data) {
    return <div className="p-6 text-sm text-gray-400">Nessun dato disponibile per questo driver.</div>;
  }

  return (
    <div className="p-6 space-y-5">

      {/* Banner Investigation + Edit bar */}
      <div className="flex items-start gap-3">
        <div className="flex-1 flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-50/60 border border-amber-100">
          <Radar className="w-4 h-4 text-amber-700 shrink-0" />
          <p className="text-xs text-amber-900 leading-relaxed">
            <span className="font-semibold">Investigation</span> · Identificazione delle filiali outlier attraverso tecniche di clustering in gruppi omogenei di filiali per budget e territorio commerciale, considerando come variabile target la percentuale di raggiungimento del budget sui ricavi lordi. La ratio sottostante tale investigation risiede nell'individuare eventuali deviazioni significative nel conseguimento del budget rispetto a cluster di filiali comparabili.
          </p>
        </div>
        <ExportXlsxButton
          fetchBlob={() => fetchExportConductRiskDriverXlsx(reportId, 'Driver12')}
          fileName={`Outlier_Clustering_Filiali_${data.period}`}
        />
        <EditActionBar
          isEditing={isEditing}
          onProcedi={() => setIsEditing(true)}
          onAnnulla={handleAnnulla}
          onSalva={handleSalva}
          disabled={isReadOnly}
        />
      </div>

      {/* Parametri */}
      <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100">
        <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
          <SlidersHorizontal className="w-4 h-4 text-amber-700" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-amber-800">Numero filiali outlier da mostrare (top&nbsp;N)</p>
          <p className="text-[10px] text-gray-400 mt-0.5">
            Prime N filiali per Modified Z-score (soglia z-score fissa {Z_THRESHOLD}) · default {data.top_n}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {autoSaving && <Loader2 className="w-3.5 h-3.5 text-amber-700/60 animate-spin" />}
          <input
            type="number"
            min={1}
            max={200}
            value={topN}
            disabled={isReadOnly || isEditing}
            onChange={e => {
              const v = parseInt(e.target.value, 10);
              if (!isNaN(v)) setTopN(Math.max(1, Math.min(200, v)));
            }}
            className="w-16 text-right text-sm font-bold text-amber-800 border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-amber-400 disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
          <div className="text-3xl font-bold text-amber-700">{data.outliers.length}</div>
          <div className="text-[11px] text-gray-400 mt-1">Outlier totali (z &gt; {Z_THRESHOLD})</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
          <div className="text-3xl font-bold text-[#1B365D]">{data.rows.length}</div>
          <div className="text-[11px] text-gray-400 mt-1">Top&nbsp;N mostrati</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
          <div className="text-3xl font-bold text-[#1B365D]">{topN}</div>
          <div className="text-[11px] text-gray-400 mt-1">Parametro N applicato</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
        <button
          onClick={() => setTab('top')}
          className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
            tab === 'top' ? 'bg-white text-amber-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Top&nbsp;N
          <span className="ml-2 inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
            {data.rows.length}
          </span>
        </button>
        <button
          onClick={() => setTab('all')}
          className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
            tab === 'all' ? 'bg-white text-amber-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Tutti gli outlier
          <span className="ml-2 inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-gray-200 text-gray-700">
            {data.outliers.length}
          </span>
        </button>
      </div>

      {/* Table */}
      {rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center text-sm text-gray-400">
          Nessuna filiale outlier in questa categoria.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 text-gray-400 uppercase text-[8px] tracking-wide">
                  <th className="text-left  px-4 py-2.5 font-semibold">CloneUo</th>
                  <th className="text-left  px-4 py-2.5 font-semibold">Filiale</th>
                  <th className="text-left  px-4 py-2.5 font-semibold">Dir. Regionale</th>
                  <th className="text-left  px-4 py-2.5 font-semibold">Area</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-amber-600">% Ragg. Bdg</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Mediana cluster</th>
                  <th className="text-right px-4 py-2.5 font-semibold">MAD cluster</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-amber-600">Z-score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rows.map((row, i) => (
                  <motion.tr
                    key={i}
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.01 }}
                    className="hover:bg-amber-50/30 transition-colors"
                  >
                    <td className="px-4 py-3 font-mono text-[10px] text-gray-500 whitespace-nowrap">
                      {isEditing ? <input type="text" className={txtCls} value={row.clone_uo} onChange={e => updateRowText(row, 'clone_uo', e.target.value)} /> : row.clone_uo}
                    </td>
                    <td className="px-4 py-3 font-medium text-[#1B365D] whitespace-nowrap">
                      {isEditing ? <input type="text" className={txtCls} value={row.branch_name} onChange={e => updateRowText(row, 'branch_name', e.target.value)} /> : row.branch_name}
                    </td>
                    <td className="px-4 py-3 text-[10px] text-gray-500 whitespace-nowrap">
                      {isEditing ? <input type="text" className={txtCls} value={row.regional_division} onChange={e => updateRowText(row, 'regional_division', e.target.value)} /> : row.regional_division}
                    </td>
                    <td className="px-4 py-3 text-[10px] text-gray-500 whitespace-nowrap">
                      {isEditing ? <input type="text" className={txtCls} value={row.area} onChange={e => updateRowText(row, 'area', e.target.value)} /> : row.area}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 font-semibold tabular-nums whitespace-nowrap">
                      {isEditing
                        ? <input type="number" step="0.01" className={numCls} value={row.pct_ragg_annuo} onChange={e => updateRowNum(row, 'pct_ragg_annuo', e.target.value)} />
                        : fmtPct(row.pct_ragg_annuo)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500 tabular-nums whitespace-nowrap">
                      {isEditing ? <input type="number" step="0.01" className={numCls} value={row.cluster_median_pct} onChange={e => updateRowNum(row, 'cluster_median_pct', e.target.value)} /> : fmtPct(row.cluster_median_pct)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500 tabular-nums whitespace-nowrap">
                      {isEditing ? <input type="number" step="0.01" className={numCls} value={row.cluster_mad} onChange={e => updateRowNum(row, 'cluster_mad', e.target.value)} /> : fmtN2(row.cluster_mad)}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {isEditing
                        ? <input type="number" step="0.01" className={numCls} value={row.modified_z_score} onChange={e => updateRowNum(row, 'modified_z_score', e.target.value)} />
                        : <span className={`inline-flex px-2 py-0.5 rounded-full font-bold text-[10px] border ${
                            row.modified_z_score > Z_THRESHOLD
                              ? 'bg-amber-50 border-amber-200 text-amber-700'
                              : 'bg-gray-50 border-gray-100 text-gray-500'
                          }`}>
                            {fmtN2(row.modified_z_score)}
                          </span>}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isEditing && (
        <div className="flex justify-end mt-2">
          <button
            type="button"
            onClick={() => addRow()}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B365D] border border-[#1B365D]/30 hover:bg-[#1B365D]/5 rounded-lg px-3 py-1.5 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Aggiungi nuovo
          </button>
        </div>
      )}
    </div>
  );
};

export default OutlierClusteringFilialiContainer;
